import React, { useEffect, useMemo, useRef, useState } from 'react';
import * as d3 from 'd3';
import CC_GO_TERMS from './ccGoTerms';

// Static hierarchical (flow-chart) alternative to the CC bar chart. The node set
// and their positions are FIXED by the hand-tuned MANUAL_POS layout below — the
// graph does not change when the underlying data changes (only the protein
// counts shown in each box update). Exactly one node (its term + sub-terms) can
// be selected at a time; selecting collapses the graph to a chip summary.

const ROOT_ID = 'GO:0005576';
const byId = new Map(CC_GO_TERMS.map(t => [t.id, t]));

// Hand-tuned layout, keyed by GO id -> [x, y] (top-left of box). This fixes both
// which terms appear and where, independent of the current data.
const MANUAL_POS = {
  "GO:0005576": [1080, 0], "GO:0072562": [828, 108], "GO:0072563": [828, 180],
  "GO:0030312": [648, 108], "GO:0140152": [540, 396], "GO:0098643": [540, 684],
  "GO:0031012": [648, 324], "GO:0005604": [756, 684], "GO:0005614": [648, 576],
  "GO:0140151": [828, 324], "GO:0043230": [1008, 108], "GO:0065010": [1008, 180],
  "GO:1903561": [1008, 252], "GO:0070062": [1008, 324], "GO:0140392": [1188, 108],
  "GO:0032311": [1296, 252], "GO:0005601": [2196, 252], "GO:0005583": [1476, 252],
  "GO:0005584": [1476, 360], "GO:0005577": [2016, 252], "GO:0042571": [1836, 252],
  "GO:0071746": [1836, 360], "GO:0071748": [1836, 468], "GO:0071749": [1656, 468],
  "GO:0071750": [1476, 576], "GO:0071752": [1476, 684], "GO:0071751": [1656, 576],
  "GO:0071754": [2016, 360], "GO:0071757": [2016, 468], "GO:0071756": [2196, 468],
  "GO:0016942": [1656, 252], "GO:0042567": [1656, 360], "GO:0034358": [1188, 360],
  "GO:0042627": [1296, 468], "GO:0034360": [1296, 576], "GO:0034364": [936, 468],
  "GO:0034365": [936, 684], "GO:0034366": [936, 576], "GO:0034362": [1476, 468],
  "GO:0034385": [1116, 468], "GO:0034363": [1116, 684], "GO:0034361": [1116, 576],
  "GO:0043083": [1368, 108],
};

const childrenOf = (() => {
  const m = new Map();
  for (const t of CC_GO_TERMS) {
    if (!t.parent) continue;
    if (!m.has(t.parent)) m.set(t.parent, []);
    m.get(t.parent).push(t.id);
  }
  return m;
})();

const subtreeCache = new Map();
function subtreeIds(id) {
  if (subtreeCache.has(id)) return subtreeCache.get(id);
  const out = new Set([id]);
  for (const c of (childrenOf.get(id) || [])) {
    for (const s of subtreeIds(c)) out.add(s);
  }
  subtreeCache.set(id, out);
  return out;
}

const ccTermId = t => (typeof t === 'string' ? t : (t?.go_id || t?.id || ''));

// Box geometry
const BOX_W = 156;
const LINE_H = 13;
const MAX_LABEL_LINES = 3;
const EDGE_CORNER = 20; // max corner radius on edge elbows (larger = softer turns)

// Fixed node set + edges, derived once from the hand-tuned layout.
const NODE_IDS = Object.keys(MANUAL_POS).filter(id => byId.has(id));
const NODE_SET = new Set(NODE_IDS);
const EDGES = NODE_IDS
  .map(id => ({ s: byId.get(id).parent, t: id }))
  .filter(e => e.s && NODE_SET.has(e.s));

export default function CcGoTermTreeFilter({ rowData, selectedGoIds, onSetSelection, isDark }) {
  const svgRef = useRef(null);

  // Rolled-up protein counts (a protein counts for its term and every ancestor).
  const counts = useMemo(() => {
    const byNode = new Map();
    if (Array.isArray(rowData)) {
      for (const row of rowData) {
        const terms = Array.isArray(row.gotermCcAll) ? row.gotermCcAll : [];
        const seen = new Set();
        for (const t of terms) {
          let cur = ccTermId(t);
          if (!byId.has(cur)) continue;
          while (cur && !seen.has(cur)) {
            seen.add(cur);
            if (!byNode.has(cur)) byNode.set(cur, new Set());
            byNode.get(cur).add(row.uniprotId);
            cur = byId.get(cur)?.parent;
          }
        }
      }
    }
    const c = new Map();
    for (const [k, v] of byNode) c.set(k, v.size);
    return c;
  }, [rowData]);

  const [open, setOpen] = useState(true);
  const isSel = id => selectedGoIds.has(id);
  // Single selection: choosing a node replaces any previous selection with that
  // term + its sub-terms; choosing the selected one clears it.
  const selectNode = (id) => {
    onSetSelection(isSel(id) ? [] : Array.from(subtreeIds(id)));
    setOpen(false);
  };
  // The one top-level selected node (if any).
  const selectedChip = CC_GO_TERMS.find(t => isSel(t.id) && !(t.parent && isSel(t.parent)));

  useEffect(() => {
    if (!open || !svgRef.current) return;

    const ctx = document.createElement('canvas').getContext('2d');
    ctx.font = '11px sans-serif';
    const innerW = BOX_W - 12;
    const wrap = (textVal) => {
      const atoms = [];
      String(textVal).split(/\s+/).filter(Boolean).forEach((word, wi) => {
        word.split('-').forEach((seg, si, arr) => {
          const text = si < arr.length - 1 ? seg + '-' : seg;
          if (!text) return;
          atoms.push({ text, glue: si === 0 ? (wi === 0 ? '' : ' ') : '' });
        });
      });
      const lines = [];
      let cur = '';
      for (const a of atoms) {
        const test = cur ? cur + a.glue + a.text : a.text;
        if (!cur || ctx.measureText(test).width <= innerW) cur = test;
        else { lines.push(cur); cur = a.text; }
      }
      if (cur) lines.push(cur);
      if (lines.length > MAX_LABEL_LINES) {
        const kept = lines.slice(0, MAX_LABEL_LINES);
        let last = kept[MAX_LABEL_LINES - 1];
        while (last && ctx.measureText(last + '…').width > innerW) last = last.slice(0, -1);
        kept[MAX_LABEL_LINES - 1] = last + '…';
        return kept;
      }
      return lines;
    };

    const meta = new Map();
    NODE_IDS.forEach(id => {
      const lines = wrap(byId.get(id).label);
      meta.set(id, { lines, count: counts.get(id) || 0, h: (lines.length + 2) * LINE_H + 8 });
    });

    const fg = isDark ? '#ddd' : '#222';
    const edgeColor = isDark ? 'rgba(255,255,255,0.30)' : 'rgba(0,0,0,0.30)';
    const boxFill = isDark ? '#2b3340' : '#eef3fb';
    const boxStroke = isDark ? '#5fa8ff' : '#2a6fc7';
    const selFill = isDark ? '#1f5aa8' : '#2a6fc7';
    const selText = '#fff';
    const hoverFill = isDark ? '#36506f' : '#dbe7fa';
    const margin = 14;

    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    NODE_IDS.forEach(id => {
      const [x, y] = MANUAL_POS[id]; const h = meta.get(id).h;
      minX = Math.min(minX, x); minY = Math.min(minY, y);
      maxX = Math.max(maxX, x + BOX_W); maxY = Math.max(maxY, y + h);
    });
    const W = (maxX - minX) + margin * 2;
    const H = (maxY - minY) + margin * 2;
    const ox = margin - minX, oy = margin - minY;

    const edgePath = (s, t) => {
      const [sx0, sy0] = MANUAL_POS[s], [tx0, ty0] = MANUAL_POS[t];
      const sx = sx0 + BOX_W / 2, sy = sy0 + meta.get(s).h;
      const tx = tx0 + BOX_W / 2, ty = ty0;
      const midY = (sy + ty) / 2;
      if (Math.abs(tx - sx) < 0.5) return `M${sx},${sy}V${ty}`;
      const dir = tx > sx ? 1 : -1;
      const r = Math.max(0, Math.min(EDGE_CORNER, Math.abs(tx - sx) / 2, Math.abs(midY - sy), Math.abs(ty - midY)));
      return `M${sx},${sy}V${midY - Math.sign(midY - sy) * r}`
        + `Q${sx},${midY} ${sx + dir * r},${midY}H${tx - dir * r}`
        + `Q${tx},${midY} ${tx},${midY + Math.sign(ty - midY) * r}V${ty}`;
    };

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();
    svg.attr('viewBox', `0 0 ${W} ${H}`).attr('width', W).attr('height', H);
    const g = svg.append('g').attr('transform', `translate(${ox},${oy})`);

    g.append('g').attr('pointer-events', 'none').selectAll('path.cc-edge')
      .data(EDGES).join('path')
      .attr('class', 'cc-edge')
      .attr('fill', 'none').attr('stroke', edgeColor).attr('stroke-width', 1.2)
      .attr('d', d => edgePath(d.s, d.t));

    const node = g.append('g').selectAll('g.cc-node')
      .data(NODE_IDS).join('g')
      .attr('class', 'cc-node')
      .attr('transform', d => `translate(${MANUAL_POS[d][0]},${MANUAL_POS[d][1]})`)
      .style('cursor', 'pointer')
      .on('click', (_e, d) => selectNode(d))
      .on('mouseover', function (_e, d) { if (!isSel(d)) d3.select(this).select('rect').attr('fill', hoverFill); })
      .on('mouseout', function (_e, d) { if (!isSel(d)) d3.select(this).select('rect').attr('fill', boxFill); });

    node.append('rect')
      .attr('width', BOX_W).attr('height', d => meta.get(d).h)
      .attr('rx', 6).attr('ry', 6)
      .attr('fill', d => isSel(d) ? selFill : boxFill)
      .attr('stroke', boxStroke).attr('stroke-width', d => isSel(d) ? 2 : 1);

    node.each(function (d) {
      const m = meta.get(d);
      const cx = BOX_W / 2;
      const total = (m.lines.length + 2) * LINE_H;
      const startY = (m.h - total) / 2 + LINE_H * 0.85;
      const t = d3.select(this).append('text')
        .attr('text-anchor', 'middle').attr('font-size', 11)
        .attr('pointer-events', 'none')
        .attr('fill', isSel(d) ? selText : fg);
      m.lines.forEach((ln, i) => t.append('tspan').attr('x', cx).attr('y', startY + i * LINE_H).text(ln));
      t.append('tspan').attr('x', cx).attr('y', startY + m.lines.length * LINE_H)
        .attr('font-size', 10).attr('opacity', 0.6).text(d);
      t.append('tspan').attr('x', cx).attr('y', startY + (m.lines.length + 1) * LINE_H)
        .attr('font-weight', 600).attr('opacity', 0.85)
        .text(`(${m.count} ${m.count === 1 ? 'protein' : 'proteins'})`);
      d3.select(this).append('title')
        .text(`${byId.get(d)?.label || d} (${m.count})\n${d}\nClick to filter (incl. sub-terms)`);
    });
  }, [open, isDark, selectedGoIds, counts]); // eslint-disable-line

  if (!open) {
    return (
      <div style={{ textAlign: 'center' }}>
        {selectedChip ? (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, justifyContent: 'center', marginBottom: 8 }}>
            <button type="button" className="pill-btn" onClick={() => onSetSelection([])}>
              ✓ {selectedChip.label} ✕
            </button>
          </div>
        ) : (
          <div style={{ opacity: 0.7, marginBottom: 8, fontSize: '0.85rem' }}>No cellular component selected.</div>
        )}
        <button type="button" className="pill-btn" onClick={() => setOpen(true)}>
          Show graph
        </button>
      </div>
    );
  }

  return (
    <div
      style={{
        maxHeight: 480, overflow: 'auto',
        border: `1px solid ${isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.12)'}`,
        borderRadius: 8, padding: 4,
      }}
    >
      <svg ref={svgRef} style={{ display: 'block' }} />
    </div>
  );
}
